import React, { useState, useEffect, useRef } from 'react';
import useApiRequest from './useApiRequest.jsx';
import { domain } from './Domain.jsx';
import { useLoadingContext } from './LoadingContext.jsx';
import { motion, AnimatePresence } from 'framer-motion';
import { 
    Plus, Edit, Trash2, Search, Filter, RefreshCw, 
    AlertCircle, CheckCircle, X, Tag, MoreVertical
} from 'lucide-react';
import notify from './Nots.jsx';

const TicketCategoryForm = ({ onAddOrUpdate, name, setName, editingId, setEditingId, inputRef, handleCancelEdit, postRequest, putRequest, setLoading }) => {
    const [errors, setErrors] = useState({});

    const validateForm = () => {
        const newErrors = {};
        if (!name.trim()) {
            newErrors.name = 'Ticket category name is required';
        } else if (name.trim().length < 2) {
            newErrors.name = 'Ticket category name must be at least 2 characters';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        
        setLoading(true);
        try {
        if (editingId) {
                const formData = new FormData();
                formData.append("name", name.trim());
                const response = await putRequest(`${domain}/ticket-categories/${editingId}`, formData, true);
            setEditingId(null);
            onAddOrUpdate(response.body);
                notify("Ticket category updated successfully", "success");
        } else {
                const formData = new FormData();
                formData.append("name", name.trim());
                const response = await postRequest(`${domain}/ticket-categories`, formData, true);
            onAddOrUpdate(response.body);
                notify("Ticket category added successfully", "success");
            }
            setName('');
            setErrors({});
        } catch (error) {
            notify("Failed to save ticket category", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setName("");
        setEditingId(null);
        setErrors({});
        inputRef.current?.focus();
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
        >
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                    <Tag className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                    <h3 className="text-lg font-semibold text-gray-800">
                        {editingId ? 'Edit Ticket Category' : 'Add New Ticket Category'}
                    </h3>
                    <p className="text-sm text-gray-600">
                        {editingId ? 'Update the ticket category information' : 'Create a new ticket category for organizing tickets'}
                    </p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        Ticket Category Name
                    </label>
                    <div className="relative">
            <input
                ref={inputRef}
                type="text"
                value={name}
                            onChange={(e) => {
                                setName(e.target.value);
                                if (errors.name) setErrors({ ...errors, name: '' });
                            }}
                            placeholder="Enter ticket category (e.g., Premium, Standard, Economy)..."
                            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200 ${
                                errors.name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                            }`}
                        />
                        {errors.name && (
                            <motion.div
                                initial={{ opacity: 0, y: -10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex items-center gap-1 mt-1 text-red-600 text-sm"
                            >
                                <AlertCircle className="w-4 h-4" />
                                {errors.name}
                            </motion.div>
                        )}
                    </div>
                </div>

                <div className="flex gap-3">
                    <button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 !text-white px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                    >
                        {editingId ? (
                            <>
                                <Edit className="w-4 h-4" />
                                Update Ticket Category
                            </>
                        ) : (
                            <>
                                <Plus className="w-4 h-4" />
                                Add Ticket Category
                            </>
                        )}
                    </button>
                    {editingId && (
                        <button
                            type="button"
                            onClick={handleCancel}
                            className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                        >
                            <X className="w-4 h-4" />
                            Cancel
                        </button>
                    )}
                </div>
        </form>
        </motion.div>
    );
};

const TicketCategoryList = ({ ticketCategories, onDelete, onEdit, searchTerm, setSearchTerm, loading }) => {
    const [sortBy, setSortBy] = useState('name');
    const [sortOrder, setSortOrder] = useState('asc');

    const filteredTicketCategories = ticketCategories.filter(ticketCategory =>
        ticketCategory.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const sortedTicketCategories = [...filteredTicketCategories].sort((a, b) => {
        const aValue = a[sortBy]?.toLowerCase() || '';
        const bValue = b[sortBy]?.toLowerCase() || '';
        return sortOrder === 'asc' 
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
    });

    if (loading) {
    return (
            <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                    <div key={i} className="bg-white rounded-xl p-6 border border-gray-200 animate-pulse">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                                <div className="space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                                    <div className="h-3 bg-gray-200 rounded w-24"></div>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                                <div className="w-16 h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (ticketCategories.length === 0) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center py-12"
            >
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Tag className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">No Ticket Categories Found</h3>
                <p className="text-gray-600 mb-6">Start by adding your first ticket category</p>
            </motion.div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Search and Filter */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search ticket categories..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        />
                    </div>
                    <div className="flex gap-2">
                        <select
                            value={`${sortBy}-${sortOrder}`}
                            onChange={(e) => {
                                const [field, order] = e.target.value.split('-');
                                setSortBy(field);
                                setSortOrder(order);
                            }}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                        >
                            <option value="name-asc">Name (A-Z)</option>
                            <option value="name-desc">Name (Z-A)</option>
                            <option value="id-asc">ID (Low-High)</option>
                            <option value="id-desc">ID (High-Low)</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Results Summary */}
            <div className="flex items-center justify-between text-sm text-gray-600">
                <p>Showing {filteredTicketCategories.length} of {ticketCategories.length} ticket categories</p>
                {searchTerm && (
                    <button
                        onClick={() => setSearchTerm('')}
                        className="text-orange-500 hover:text-orange-600 font-medium"
                    >
                        Clear search
                    </button>
                )}
            </div>

            {/* Ticket Categories List */}
            <div className="space-y-3">
                <AnimatePresence>
                    {sortedTicketCategories.map((ticketCategory, index) => (
                        <motion.div
                            key={ticketCategory.id}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -20 }}
                            transition={{ delay: index * 0.05 }}
                            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all duration-200"
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-orange-200 rounded-xl flex items-center justify-center">
                                        <Tag className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-800">{ticketCategory.name}</h4>
                                        <p className="text-sm text-gray-500">ID: {ticketCategory.id}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <button
                                        onClick={() => onEdit(ticketCategory)}
                                        className="px-4 py-2 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                                    >
                                        <Edit className="w-4 h-4" />
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => onDelete(ticketCategory.id)}
                                        className="px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
        </div>
    );
};

const TicketCategoryComponent = () => {
    const { setLoading } = useLoadingContext();
    const inputRef = useRef();
    const [name, setName] = useState('');
    const [editingId, setEditingId] = useState(null);
    const [ticketCategories, setTicketCategories] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLocalLoading] = useState(false);
    const { postRequest, getRequest, putRequest, deleteRequest } = useApiRequest();

    useEffect(() => {
        fetchTicketCategories();
    }, []);

    const fetchTicketCategories = async () => {
        setLocalLoading(true);
        try {
            const response = await getRequest(`${domain}/ticket-categories`);
            setTicketCategories(response.body || []);
            } catch (error) {
            console.error('Error fetching ticket categories:', error);
            notify("Failed to load ticket categories", "error");
            } finally {
            setLocalLoading(false);
        }
    };

    const handleAddOrUpdate = (ticketCategory) => {
        const index = ticketCategories.findIndex((t) => t.id === ticketCategory.id);
        if (index === -1) {
            setTicketCategories([ticketCategory, ...ticketCategories]);
        } else {
            const newTicketCategories = [...ticketCategories];
            newTicketCategories[index] = ticketCategory;
            setTicketCategories(newTicketCategories);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to delete this ticket category? This action cannot be undone.")) {
            setLoading(true);
            try {
                await deleteRequest(`${domain}/ticket-categories/${id}`);
                setTicketCategories(ticketCategories.filter((ticketCategory) => ticketCategory.id !== id));
                notify("Ticket category deleted successfully", "success");
            } catch (error) {
                notify("Failed to delete ticket category", "error");
            } finally {
                setLoading(false);
            }
        }
    };

    const handleEdit = (ticketCategory) => {
        setName(ticketCategory.name);
        setEditingId(ticketCategory.id);
        inputRef.current?.focus();
    };

    const handleCancelEdit = () => {
        setName("");
        setEditingId(null);
        inputRef.current?.focus();
    };

    return (
        <div className="space-y-6">
            <TicketCategoryForm 
                onAddOrUpdate={handleAddOrUpdate} 
                name={name} 
                setName={setName} 
                editingId={editingId} 
                setEditingId={setEditingId} 
                inputRef={inputRef} 
                handleCancelEdit={handleCancelEdit} 
                postRequest={postRequest} 
                putRequest={putRequest} 
                setLoading={setLoading} 
            />
            
            <TicketCategoryList
                ticketCategories={ticketCategories}
                onDelete={handleDelete}
                onEdit={handleEdit}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                loading={loading}
            />
        </div>
    );
};

export default TicketCategoryComponent;